import { z } from "@hono/zod-openapi";

/**
 * What every `callRejection` kind states at version 1: the TMS's own account of why it refused the call.
 *
 * Declared once and shared by every kind in the category, because the kinds differ only in which family of reason
 * the vendor's code falls under — the fields are the same.
 */
export const callRejectionFacts = {
  /** The TMS's processing code for the refusal, verbatim (`106`, `097`, …). */
  statusCode: z.string(),
  /** The vendor's short name for the code (`NotArrSt`, `InvDrpTr`, …), padding trimmed. */
  statusDesc: z.string(),
  /** The vendor's message for the code, as it would show the driver. */
  statusMsg: z.string(),
};

/** Which call the driver sent, as the refused call's header line names it. */
export const RejectedCallTypeSchema = z.enum(["loadedCall", "emptyCall", "arrival"]).openapi("RejectedCallType");

export type RejectedCallType = z.infer<typeof RejectedCallTypeSchema>;

/**
 * What every `callRejection` kind states at version 2: the TMS's account, plus what the driver's call said.
 *
 * The call is the form the driver sent, echoed back in the refusal. A field is null when the call's template has no
 * such line or the driver left it blank, and otherwise carries what the driver typed, placeholders included: a
 * trailer of `N/A` is the diagnosis, not noise.
 */
export const callRejectionV2Facts = {
  ...callRejectionFacts,
  /** Null when no line of the call is one of the three headers. */
  callType: RejectedCallTypeSchema.nullable(),
  /** The load the driver named; loaded calls only. */
  loadNumber: z.string().nullable(),
  /** The trailer the driver said they dropped. */
  dropTrailerNumber: z.string().nullable(),
  /** The trailer the driver said they are hooked to now. */
  currentTrailerNumber: z.string().nullable(),
  /** The consignee the driver named; empty calls only, and cut at the 40th column. */
  consigneeName: z.string().nullable(),
  /**
   * The call verbatim, as its 40-column lines, trailing padding trimmed and trailing blank lines dropped.
   *
   * For display alone. Nothing parses it: a field anyone needs to act on gets its own fact.
   */
  callLines: z.array(z.string()),
};
